import { type RefObject, useCallback, useEffect, useRef } from "react";

const MIN_SWIPE_DIST = 80;
const CAPTURE_THRESHOLD = 8;

export interface SwipeableComponentRef {
	swipeLeft: () => void;
	swipeRight: () => void;
}

export const useSwipeable = (
	componentRef: RefObject<SwipeableComponentRef | null>,
	automaticSwipe = true,
	delay = 5000,
) => {
	const interval = useRef<ReturnType<typeof setInterval> | null>(null);
	const startX = useRef<number | null>(null);
	const lastX = useRef<number | null>(null);
	const activePointerId = useRef<number | null>(null);

	const startChange = useCallback(() => {
		if (!automaticSwipe) return;

		interval.current = setInterval(() => {
			componentRef.current?.swipeRight();
		}, delay);
	}, [automaticSwipe, delay, componentRef]);

	const stopChange = useCallback(() => {
		if (interval.current) {
			clearInterval(interval.current);
			interval.current = null;
		}
	}, []);

	useEffect(() => {
		startChange();

		return () => stopChange();
	}, [startChange, stopChange]);

	const finishGesture = () => {
		if (startX.current === null || lastX.current === null) return;

		const deltaX = lastX.current - startX.current;

		if (Math.abs(deltaX) > MIN_SWIPE_DIST) {
			if (deltaX > 0) {
				componentRef.current?.swipeLeft();
			} else {
				componentRef.current?.swipeRight();
			}
		}

		startX.current = null;
		lastX.current = null;
		activePointerId.current = null;
	};

	const handlePointerDown = (e: React.PointerEvent) => {
		if (activePointerId.current !== null) return;
		activePointerId.current = e.pointerId;
		startX.current = e.clientX;
		lastX.current = e.clientX;
	};

	const handlePointerMove = (e: React.PointerEvent) => {
		if (e.pointerId !== activePointerId.current) return;
		lastX.current = e.clientX;

		if (
			startX.current !== null &&
			Math.abs(e.clientX - startX.current) > CAPTURE_THRESHOLD &&
			!e.currentTarget.hasPointerCapture(e.pointerId)
		) {
			e.currentTarget.setPointerCapture(e.pointerId);
		}
	};

	const handlePointerEnd = (e: React.PointerEvent) => {
		if (e.pointerId !== activePointerId.current) return;
		finishGesture();
	};

	return {
		handlePointerDown,
		handlePointerMove,
		handlePointerEnd,
		startChange,
		stopChange,
	};
};
